import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { getApiBaseUrl, getCsrfToken } from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import './Products.css';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number; // Base price before discount
  category: string;
  categoryId?: string;
  stock: number;
  imageUrl?: string;
}

interface ShoppingListResult {
  item: string;
  matches: Product[];
  selectedProductId?: string;
}

type ProductCardProps = {
  product: Product;
  quantity: number;
  membershipDiscountPercent: number;
  onQuantityChange: (productId: string, quantity: number) => void;
  onAddToCart: (product: Product) => void;
  formatCategory: (category: string) => string;
};

const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({ product, quantity, membershipDiscountPercent, onQuantityChange, onAddToCart, formatCategory }) => (
    <div className="product-card">
      <Link to={`/products/${product.id}`} className="product-image-container product-link">
        {product.imageUrl ? (
          <img 
            src={product.imageUrl} 
            alt={product.name}
            className="product-image"
            loading="lazy"
          />
        ) : (
          <div className="product-image-placeholder">No image</div>
        )}
      </Link>
      <div className="product-card-content">
        <div className="product-category">{formatCategory(product.category)}</div>
        <h3>
          <Link to={`/products/${product.id}`} className="product-title-link">
            {product.name}
          </Link>
        </h3>
        <div className="product-pricing">
          <div className="price-row">
            <span className="product-price">
              ${(product.price * (1 - membershipDiscountPercent / 100)).toFixed(2)}
            </span>
            {membershipDiscountPercent > 0 && (
              <span className="discount-badge">{membershipDiscountPercent}% member discount</span>
            )}
          </div>
          {membershipDiscountPercent > 0 && (
            <div className="base-price">Base price ${product.price.toFixed(2)}</div>
          )}
        </div>
        <div className="product-actions">
          <input
            type="number"
            min="1"
            max={product.stock}
            value={quantity}
            onChange={(e) =>
              onQuantityChange(product.id, parseInt(e.target.value) || 1)
            }
            className="quantity-input"
            disabled={product.stock === 0}
          />
          <button
            className="btn btn-primary"
            onClick={() => onAddToCart(product)}
            disabled={product.stock === 0}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  )
);

const Products: React.FC = () => {
  const formatCategory = (category: string) => {
    const parts = category.split('>').map(part => part.trim()).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : category;
  };
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'search' | 'list'>(() => {
    const stored = localStorage.getItem('productsSearchMode');
    return stored === 'list' ? 'list' : 'search';
  });
  const [shoppingListText, setShoppingListText] = useState(() => localStorage.getItem('shoppingListText') || '');
  const [shoppingResults, setShoppingResults] = useState<ShoppingListResult[]>([]);
  const [shoppingListTotal, setShoppingListTotal] = useState<number | null>(null);
  const [shoppingListFilter, setShoppingListFilter] = useState('');
  const [shoppingSuggestions, setShoppingSuggestions] = useState<Product[]>([]);
  const [shoppingLoading, setShoppingLoading] = useState(false);
  const [shoppingError, setShoppingError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [membershipDiscountPercent, setMembershipDiscountPercent] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { user } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    localStorage.setItem('productsSearchMode', searchMode);
  }, [searchMode]);

  useEffect(() => {
    localStorage.setItem('shoppingListText', shoppingListText);
  }, [shoppingListText]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data.products);
      setMembershipDiscountPercent(response.data.membershipDiscountPercent || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredShoppingResults = useMemo(() => {
    const q = shoppingListFilter.trim().toLowerCase();
    if (!q) return shoppingResults;
    return shoppingResults.filter(
      (r) =>
        r.item.toLowerCase().includes(q) ||
        r.matches.some((p) => p.name.toLowerCase().includes(q) || (p.category && p.category.toLowerCase().includes(q)))
    );
  }, [shoppingResults, shoppingListFilter]);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(products.map(product => product.category).filter(Boolean)))],
    [products]
  );

  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        product =>
          product.name.toLowerCase().includes(query) ||
          product.description.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query)
      );
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    if (priceRange !== 'all') {
      switch (priceRange) {
        case 'under-5':
          filtered = filtered.filter(product => product.price < 5);
          break;
        case '5-10':
          filtered = filtered.filter(product => product.price >= 5 && product.price < 10);
          break;
        case 'over-10':
          filtered = filtered.filter(product => product.price >= 10);
          break;
      }
    }
    return filtered;
  }, [products, priceRange, searchQuery, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, priceRange]);

  const handleQuantityChange = useCallback((productId: string, quantity: number) => {
    setQuantities(prev => ({ ...prev, [productId]: quantity }));
  }, []);

  const addToCart = useCallback(async (product: Product) => {
    if (!user) {
      showError('Please login or register to add items to cart');
      navigate('/login', { state: { returnTo: '/cart' } });
      return;
    }

    const quantity = quantities[product.id] || 1;
    try {
      await api.post('/cart/add', {
        productId: product.id,
        quantity: parseInt(String(quantity)),
      });
      showSuccess('Item added to cart!');
      setQuantities({ ...quantities, [product.id]: 1 });
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Please login to add items to cart');
        navigate('/login', { state: { returnTo: '/cart' } });
      } else {
        showError(error.response?.data?.error || 'Failed to add item to cart');
      }
    }
  }, [navigate, showError, showSuccess, user, quantities]);

  const runShoppingListSearch = async () => {
    if (!shoppingListText.trim()) {
      setShoppingError('Please enter a shopping list');
      return;
    }
    setShoppingLoading(true);
    setShoppingError('');
    setShoppingResults([]);
    setShoppingSuggestions([]);
    setShoppingListTotal(null);
    const url = `${getApiBaseUrl()}/products/shopping-list`;
    const csrf = getCsrfToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (csrf) headers['x-csrf-token'] = csrf;
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ list: shoppingListText }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || 'Unable to search shopping list');
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Stream not supported');
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const dataMatch = line.match(/^data: (.+)/s);
          if (!dataMatch) continue;
          try {
            const data = JSON.parse(dataMatch[1].trim());
            if (data.type === 'start') {
              setShoppingListTotal(data.total ?? null);
            } else if (data.type === 'item') {
              setShoppingResults((prev) => [
                ...prev,
                { item: data.item, matches: data.matches || [], selectedProductId: undefined },
              ]);
            } else if (data.type === 'suggestions') {
              setShoppingSuggestions(data.suggestions || []);
            } else if (data.type === 'error') {
              setShoppingError(data.message || 'Unable to process list');
            }
          } catch (_) {}
        }
      }
    } catch (err: any) {
      setShoppingError(err?.message || 'Unable to search shopping list');
    } finally {
      setShoppingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="page-header">
          <h1>Our Products</h1>
          <p>Browse our premium selection of groceries</p>
        </div>
        <div className="products-skeleton-grid">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="product-skeleton-card">
              <div className="product-skeleton-image" />
              <div className="product-skeleton-line title" />
              <div className="product-skeleton-line price" />
              <div className="product-skeleton-line action" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Our Products</h1>
        <p>Browse our premium selection of groceries</p>
      </div>

      {/* Search and Filter Section */}
      <div className="products-filters">
        <div className="search-mode-toggle">
          <button
            type="button"
            className={`mode-toggle-btn ${searchMode === 'search' ? 'active' : ''}`}
            onClick={() => setSearchMode('search')}
          >
            Search Products
          </button>
          <button
            type="button"
            className={`mode-toggle-btn ${searchMode === 'list' ? 'active' : ''}`}
            onClick={() => setSearchMode('list')}
          >
            Shopping List
          </button>
        </div>

        {searchMode === 'search' && (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            
            <button
              className="filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </>
        )}

        {searchMode === 'list' && (
          <div className="shopping-list-input">
            <label>Paste your shopping list (comma separated or new line)</label>
            <textarea
              value={shoppingListText}
              onChange={(e) => setShoppingListText(e.target.value)}
              rows={4}
              placeholder="Milk, Eggs, Bread&#10;Rice&#10;Chicken breast"
            />
            <div className="shopping-list-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={runShoppingListSearch}
                disabled={shoppingLoading}
              >
                {shoppingLoading ? 'Searching...' : 'Find Products'}
              </button>
              {shoppingError && <div className="error">{shoppingError}</div>}
            </div>
          </div>
        )}

        {showFilters && searchMode === 'search' && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Price Range</label>
              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Prices</option>
                <option value="under-5">Under $5</option>
                <option value="5-10">$5 - $10</option>
                <option value="over-10">Over $10</option>
              </select>
            </div>

            <button
              className="clear-filters"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPriceRange('all');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {searchMode === 'search' ? (
        <>
          {filteredProducts.length === 0 ? (
            <div className="results-empty">
              <p>No products found. Try adjusting your filters.</p>
              <div className="results-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setPriceRange('all');
                  }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="results-info">
                <p>
                  Showing {Math.min(startIndex + 1, filteredProducts.length)}-
                  {Math.min(startIndex + paginatedProducts.length, filteredProducts.length)} of{' '}
                  {filteredProducts.length} products
                </p>
              </div>
              <div className="products-grid">
                {paginatedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    quantity={quantities[product.id] || 1}
                    membershipDiscountPercent={membershipDiscountPercent}
                    onQuantityChange={handleQuantityChange}
                    onAddToCart={addToCart}
                    formatCategory={formatCategory}
                  />
                ))}
              </div>
              {filteredProducts.length > itemsPerPage && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="pagination-label">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="shopping-results">
          {shoppingResults.length === 0 && !shoppingLoading && !shoppingError ? (
            <div className="results-info">
              <p>Enter items to generate shopping list results.</p>
            </div>
          ) : (
            <>
              {shoppingLoading && (
                <p className="shopping-list-progress" role="status">
                  {shoppingListTotal != null
                    ? `Finding products… ${shoppingResults.length} of ${shoppingListTotal} ready`
                    : 'Finding products… Results will appear as they\'re ready.'}
                </p>
              )}
              {shoppingResults.length > 0 && !shoppingLoading && (
                <div className="shopping-list-filter">
                  <input
                    type="text"
                    placeholder="Filter results by item or product name..."
                    value={shoppingListFilter}
                    onChange={(e) => setShoppingListFilter(e.target.value)}
                    className="search-input"
                    aria-label="Filter shopping list results"
                  />
                  {shoppingListFilter && (
                    <span className="shopping-list-filter-count">
                      {filteredShoppingResults.length} of {shoppingResults.length} items
                    </span>
                  )}
                </div>
              )}
              {filteredShoppingResults.length === 0 && shoppingListFilter ? (
                <div className="results-info">
                  <p>No results match &quot;{shoppingListFilter}&quot;. Try a different filter.</p>
                </div>
              ) : (
              filteredShoppingResults.map((result) => {
                const originalIndex = shoppingResults.indexOf(result);
                return (
                <div key={`${result.item}-${originalIndex}`} className="shopping-result-row">
                  <div className="shopping-result-title">{result.item}</div>
                  <div className="shopping-result-scroll">
                    {result.matches.length === 0 ? (
                      <div className="shopping-result-empty">No matching products found.</div>
                    ) : (
                      result.matches.map((product) => {
                        const qty = Math.max(1, Math.min(product.stock || 999, quantities[product.id] ?? 1));
                        return (
                        <div key={product.id} className="product-card compact">
                          <Link to={`/products/${product.id}`} className="product-image-container product-link">
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt={product.name} className="product-image" loading="lazy" />
                            ) : (
                              <div className="product-image-placeholder">No image</div>
                            )}
                          </Link>
                          <div className="product-card-content">
                            <div className="product-category">{formatCategory(product.category)}</div>
                            <h3>
                              <Link to={`/products/${product.id}`} className="product-title-link">
                                {product.name}
                              </Link>
                            </h3>
                            <div className="product-pricing">
                              <span className="product-price">
                                ${(product.price * (1 - membershipDiscountPercent / 100)).toFixed(2)}
                              </span>
                            </div>
                            <div className="shopping-list-card-actions">
                              <label className="shopping-list-qty-label">
                                <span className="visually-hidden">Quantity for {product.name}</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={product.stock || 999}
                                  value={qty}
                                  onChange={(e) => handleQuantityChange(product.id, Math.max(1, Math.min(product.stock || 999, parseInt(e.target.value, 10) || 1)))}
                                  className="shopping-list-qty-input"
                                  aria-label="Quantity"
                                />
                              </label>
                              <button
                                type="button"
                                className="btn btn-primary product-card-action"
                                onClick={() => addToCart(product)}
                                disabled={product.stock === 0}
                              >
                                Add to Cart
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
              })
              )}

              {shoppingSuggestions.length > 0 && (
                <div className="shopping-suggestions">
                  <h3 className="shopping-suggestions-heading">You might also need</h3>
                  <p className="shopping-suggestions-sub">Based on your list—add these to complete your shop.</p>
                  <div className="shopping-result-scroll shopping-suggestions-scroll">
                    {shoppingSuggestions.map((product) => (
                      <div key={product.id} className="product-card compact">
                        <Link to={`/products/${product.id}`} className="product-image-container product-link">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="product-image" loading="lazy" />
                          ) : (
                            <div className="product-image-placeholder">No image</div>
                          )}
                        </Link>
                        <div className="product-card-content">
                          <div className="product-category">{formatCategory(product.category)}</div>
                          <h3>
                            <Link to={`/products/${product.id}`} className="product-title-link">
                              {product.name}
                            </Link>
                          </h3>
                          <div className="product-pricing">
                            <span className="product-price">
                              ${(product.price * (1 - membershipDiscountPercent / 100)).toFixed(2)}
                            </span>
                          </div>
                          <button
                            className="btn btn-secondary product-card-action"
                            onClick={() => addToCart(product)}
                            disabled={product.stock === 0}
                          >
                            Add Suggestion
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Products;

